import { FieldType, IFilter } from "@saleor/components/Filter";
import { AutocompleteFilterOpts, FilterOpts, MinMax } from "@saleor/types";
import {
  CourierTypeEnum,
  DeliveryTypeEnum,
  RemittanceStatusEnum,
  StaffNameInput
} from "@saleor/types/globalTypes";
import {
  createAutocompleteField,
  createDateField,
  createMultipleTextField,
  createOptionsField,
  createTextField
} from "@saleor/utils/filters/fields";
import { defineMessages, IntlShape } from "react-intl";

const messages = defineMessages({
  amount: {
    defaultMessage: "Amount",
    description: "amount"
  },
  companyName: {
    defaultMessage: "Company Name",
    description: "company name"
  },
  courierGrab: {
    defaultMessage: "Grab",
    description: "courier grab"
  },
  courierInHouse: {
    defaultMessage: "In House",
    description: "courier in house"
  },
  courierLalamove: {
    defaultMessage: "Lalamove",
    description: "courier lalamove"
  },
  courierOthers: {
    defaultMessage: "Others",
    description: "courier others"
  },
  courierPandago: {
    defaultMessage: "Pandago",
    description: "courier pandago"
  },
  createdDate: {
    defaultMessage: "Created Date",
    description: "created date"
  },
  dateCompleted: {
    defaultMessage: "Date Completed",
    description: "date completed"
  },
  dateShipped: {
    defaultMessage: "Date Shipped",
    description: "date shipped"
  },
  deliveryType: {
    defaultMessage: "Delivery Type",
    description: "delivery type"
  },
  dispatcher: {
    defaultMessage: "Dispatcher",
    description: "dispatcher"
  },
  express: {
    defaultMessage: "Express",
    description: "express"
  },
  expressLongDistance: {
    defaultMessage: "10km Express Delivery",
    description: "expressLongDistance"
  },
  inStore: {
    defaultMessage: "In Store Pickup",
    description: "in store pickup"
  },
  isPaymentCollected: {
    defaultMessage: "For Payment Collection",
    description: "remittance status is for payment collection"
  },

  mall: {
    defaultMessage: "Mall",
    description: "mall"
  },
  motherOrder: {
    defaultMessage: "Mother Order",
    description: "mother order"
  },
  nationwide: {
    defaultMessage: "Nationwide Delivery",
    description: "nationwide delivery"
  },
  nextDay: {
    defaultMessage: "Standard Delivery",
    description: "standard delivery"
  },
  paymentCollection: {
    defaultMessage: "Payment Collection",
    description: "remittance status is for payment collection"
  },
  receiptUploaded: {
    defaultMessage: "Receipt Uploaded",
    description: "proof of remittance is uploaded"
  },
  remittanceDate: {
    defaultMessage: "Remittance Date",
    description: "date shipped"
  },
  remittanceId: {
    defaultMessage: "Remittance ID",
    description: "remittance id"
  },
  remitter: {
    defaultMessage: "Remitter",
    description: "remitter"
  },
  riderCourier: {
    defaultMessage: "Courier / Rider",
    description: "courier / rider"
  },
  sameDay: {
    defaultMessage: "Same Day Delivery",
    description: "same day delivery"
  },
  status: {
    defaultMessage: "Status",
    description: "remittance status"
  },
  statusForConfirmation: {
    defaultMessage: "For Confirmation",
    description: "remittance status is to for bank confirmation"
  },
  statusNoRemittanceId: {
    defaultMessage: "No Remittance ID",
    description: "order with no remittance ID yet"
  },
  statusRemitted: {
    defaultMessage: "Remitted",
    description: "remittance status is to remitted"
  },
  statusToRemit: {
    defaultMessage: "To Remit",
    description: "remittance status is to remit"
  }
});

export enum RemittanceRunnerFilterKeys {
  motherOrder = "motherOrder",
  status = "status",
  dateShipped = "dateShipped",
  dateShippedFrom = "dateShippedFrom",
  dateShippedTo = "dateShippedTo",
  dateCompleted = "dateCompleted",
  deliveryType = "deliveryType",
  amount = "amount",
  remittanceId = "remittanceId",
  remittanceDate = "remittanceDate",
  remittanceDateFrom = "remittanceDateFrom",
  remittanceDateTo = "remittanceDateTo",
  remitterName = "remitterName",
  remitterFirstName = "remitterFirstName",
  remitterLastName = "remitterLastName",
  dispatcherName = "dispatcherName",
  dispatcherFirstName = "dispatcherFirstName",
  dispatcherLastName = "dispatcherLastName",
  riderName = "riderName",
  riderFirstName = "riderFirstName",
  riderLastName = "riderLastName",
  mall = "mall",
  isPaymentCollected = "isPaymentCollected",
  createdDate = "createdDate",
  createdDateFrom = "createdDateFrom",
  createdDateTo = "createdDateTo",
  hasReceipt = "hasReceipt",
  companyName = "companyName"
}

export interface RemittanceRunnerFilterOpts {
  motherOrder?: FilterOpts<string>;
  remittanceId?: FilterOpts<string>;
  remittanceDate?: FilterOpts<MinMax>;
  status?: FilterOpts<RemittanceStatusEnum | string>;
  remitterName?: FilterOpts<StaffNameInput>;
  dispatcherName?: FilterOpts<StaffNameInput>;
  dateShipped?: FilterOpts<MinMax>;
  dateCompleted?: FilterOpts<MinMax>;
  deliveryType?: FilterOpts<DeliveryTypeEnum[]>;
  riderName?: FilterOpts<StaffNameInput>;
  amount?: FilterOpts<string>;
  mall?: FilterOpts<string[]> & AutocompleteFilterOpts;
  search?: FilterOpts<string>;
  isPaymentCollected?: FilterOpts<boolean>;
  createdDate?: FilterOpts<MinMax>;
  hasReceipt?: FilterOpts<boolean>;
  companyName?: FilterOpts<CourierTypeEnum[]>;
}

const motherOrderOption = (intl, opts) => ({
  ...createTextField(
    RemittanceRunnerFilterKeys.motherOrder,
    intl.formatMessage(messages.motherOrder),
    opts?.motherOrder?.value
  ),
  active: opts?.motherOrder?.active
});

const statusOption = (intl, opts) => ({
  ...createOptionsField(
    RemittanceRunnerFilterKeys.status,
    intl.formatMessage(messages.status),
    [opts?.status?.value],
    false,
    [
      {
        label: intl.formatMessage(messages.statusToRemit),
        value: RemittanceStatusEnum.TO_REMIT
      },
      {
        label: intl.formatMessage(messages.statusForConfirmation),
        value: RemittanceStatusEnum.FOR_BANK_CONFIRMATION
      },
      {
        label: intl.formatMessage(messages.statusRemitted),
        value: RemittanceStatusEnum.REMITTED
      }
    ]
  ),
  active: opts?.status?.active
});

const statusWithNoRemittanceIdOption = (intl, opts) => ({
  ...createOptionsField(
    RemittanceRunnerFilterKeys.status,
    intl.formatMessage(messages.status),
    [opts?.status?.value],
    false,
    [
      {
        label: intl.formatMessage(messages.statusToRemit),
        value: RemittanceStatusEnum.TO_REMIT
      },
      {
        label: intl.formatMessage(messages.statusForConfirmation),
        value: RemittanceStatusEnum.FOR_BANK_CONFIRMATION
      },
      {
        label: intl.formatMessage(messages.statusRemitted),
        value: RemittanceStatusEnum.REMITTED
      },
      {
        label: intl.formatMessage(messages.statusNoRemittanceId),
        value: "NO_REMITTANCE_ID"
      }
    ]
  ),
  active: opts?.status?.active
});

const dateShippedOption = (intl, opts) => ({
  ...createDateField(
    RemittanceRunnerFilterKeys.dateShipped,
    intl.formatMessage(messages.dateShipped),
    opts?.dateShipped.value
  ),
  active: opts?.dateShipped.active
});

const dateCompletedOption = (intl, opts) => ({
  ...createDateField(
    RemittanceRunnerFilterKeys.dateCompleted,
    intl.formatMessage(messages.dateCompleted),
    opts?.dateCompleted.value
  ),
  active: opts?.dateCompleted.active
});

const remittanceDateOption = (intl, opts) => ({
  ...createDateField(
    RemittanceRunnerFilterKeys.remittanceDate,
    intl.formatMessage(messages.remittanceDate),
    opts?.remittanceDate?.value
  ),
  active: opts?.remittanceDate?.active
});

const remitterOption = (intl, opts) => ({
  ...createMultipleTextField(
    RemittanceRunnerFilterKeys.remitterName,
    intl.formatMessage(messages.remitter),
    [
      {
        active: opts?.remitterName?.active,
        label: "First name",
        multiple: false,
        name: RemittanceRunnerFilterKeys.remitterFirstName,
        type: FieldType.text,
        value: opts?.remitterName?.value?.firstName
      },
      {
        active: opts?.remitterName?.active,
        label: "Last name",
        multiple: false,
        name: RemittanceRunnerFilterKeys.remitterLastName,
        type: FieldType.text,
        value: opts?.remitterName?.value?.lastName
      }
    ]
  ),
  active: opts?.remitterName?.active
});

const dispatcherOption = (intl, opts) => ({
  ...createMultipleTextField(
    RemittanceRunnerFilterKeys.dispatcherName,
    intl.formatMessage(messages.dispatcher),
    [
      {
        active: opts?.dispatcherName?.active,
        label: "First name",
        multiple: false,
        name: RemittanceRunnerFilterKeys.dispatcherFirstName,
        type: FieldType.text,
        value: opts?.dispatcherName?.value?.firstName
      },
      {
        active: opts?.dispatcherName?.active,
        label: "Last name",
        multiple: false,
        name: RemittanceRunnerFilterKeys.dispatcherLastName,
        type: FieldType.text,
        value: opts?.dispatcherName?.value?.lastName
      }
    ]
  ),
  active: opts?.dispatcherName?.active
});

const riderNameOption = (intl, opts) => ({
  ...createMultipleTextField(
    RemittanceRunnerFilterKeys.riderName,
    intl.formatMessage(messages.riderCourier),
    [
      {
        active: opts?.riderName?.active,
        label: "First name",
        multiple: false,
        name: RemittanceRunnerFilterKeys.riderFirstName,
        type: FieldType.text,
        value: opts?.riderName?.value?.firstName
      },
      {
        active: opts?.riderName?.active,
        label: "Last name",
        multiple: false,
        name: RemittanceRunnerFilterKeys.riderLastName,
        type: FieldType.text,
        value: opts?.riderName?.value?.lastName
      }
    ]
  ),
  active: opts?.riderName?.active
});

const remittanceIdOption = (intl, opts) => ({
  ...createTextField(
    RemittanceRunnerFilterKeys.remittanceId,
    intl.formatMessage(messages.remittanceId),
    opts?.remittanceId?.value
  ),
  active: opts?.remittanceId?.active
});

const deliveryTypeOption = (intl, opts) => ({
  ...createOptionsField(
    RemittanceRunnerFilterKeys.deliveryType,
    intl.formatMessage(messages.deliveryType),
    opts?.deliveryType?.value,
    true,
    [
      // NOTE: ONLY INCLUDE DELIVERY TYPES FOR REMITTANCE
      {
        label: intl.formatMessage(messages.express),
        value: DeliveryTypeEnum.EXPRESS
      },
      {
        label: intl.formatMessage(messages.expressLongDistance),
        value: DeliveryTypeEnum.EXPRESS_LONG_DISTANCE
      },
      {
        label: intl.formatMessage(messages.nextDay),
        value: DeliveryTypeEnum.NEXT_DAY
      },
      {
        label: intl.formatMessage(messages.sameDay),
        value: DeliveryTypeEnum.SAME_DAY
      }
    ]
  ),
  active: opts?.deliveryType?.active
});

const amountOption = (intl, opts) => ({
  ...createTextField(
    RemittanceRunnerFilterKeys.amount,
    intl.formatMessage(messages.amount),
    opts?.amount?.value
  ),
  active: opts?.amount?.active
});

const mallOption = (intl, opts) => ({
  ...createAutocompleteField(
    RemittanceRunnerFilterKeys.mall,
    intl.formatMessage(messages.mall),
    opts?.mall?.value,
    opts?.mall?.displayValues,
    true,
    opts?.mall?.choices,
    {
      hasMore: opts?.mall?.hasMore,
      initialSearch: "",
      loading: opts?.mall?.loading,
      onFetchMore: opts?.mall?.onFetchMore,
      onSearchChange: opts?.mall?.onSearchChange
    }
  ),
  active: opts?.mall?.active
});

const forPaymentCollectionOption = (intl, opts, label) => ({
  ...createOptionsField(
    RemittanceRunnerFilterKeys.isPaymentCollected,
    intl.formatMessage(label),
    [opts?.isPaymentCollected?.value],
    false,
    [
      {
        label: "Yes",
        value: true
      },
      {
        label: "No",
        value: false
      }
    ]
  ),
  active: opts?.isPaymentCollected?.active
});

const createdDateOption = (intl, opts) => ({
  ...createDateField(
    RemittanceRunnerFilterKeys.createdDate,
    intl.formatMessage(messages.createdDate),
    opts?.createdDate?.value
  ),
  active: opts?.createdDate?.active
});

const hasReceiptOption = (intl, opts) => ({
  ...createOptionsField(
    RemittanceRunnerFilterKeys.hasReceipt,
    intl.formatMessage(messages.receiptUploaded),
    [opts?.hasReceipt?.value],
    false,
    [
      {
        label: "Yes",
        value: true
      },
      {
        label: "No",
        value: false
      }
    ]
  ),
  active: opts?.hasReceipt?.active
});

const companyOption = (intl, opts) => ({
  ...createOptionsField(
    RemittanceRunnerFilterKeys.companyName,
    intl.formatMessage(messages.companyName),
    [opts?.company?.value],
    true,
    [
      {
        label: intl.formatMessage(messages.courierInHouse),
        value: CourierTypeEnum.IN_HOUSE
      },
      {
        label: intl.formatMessage(messages.courierGrab),
        value: CourierTypeEnum.GRAB
      },
      {
        label: intl.formatMessage(messages.courierLalamove),
        value: CourierTypeEnum.LALAMOVE
      },
      {
        label: intl.formatMessage(messages.courierPandago),
        value: CourierTypeEnum.PANDAGO
      },
      {
        label: intl.formatMessage(messages.courierOthers),
        value: CourierTypeEnum.OTHERS
      }
    ]
  ),
  active: opts?.company?.active
});

export function createFilterStructureForOmiAdmin(
  intl: IntlShape,
  opts: RemittanceRunnerFilterOpts
): IFilter<RemittanceRunnerFilterKeys> {
  return [
    motherOrderOption(intl, opts),
    statusWithNoRemittanceIdOption(intl, opts),
    remittanceDateOption(intl, opts),
    remitterOption(intl, opts),
    remittanceIdOption(intl, opts),
    mallOption(intl, opts),
    forPaymentCollectionOption(intl, opts, messages.paymentCollection),
    hasReceiptOption(intl, opts),
    companyOption(intl, opts)
  ];
}

export function createFilterStructureForNonDispatcher(
  intl: IntlShape,
  opts: RemittanceRunnerFilterOpts
): IFilter<RemittanceRunnerFilterKeys> {
  return [
    motherOrderOption(intl, opts),
    statusWithNoRemittanceIdOption(intl, opts),
    remittanceDateOption(intl, opts),
    remitterOption(intl, opts),
    remittanceIdOption(intl, opts),
    forPaymentCollectionOption(intl, opts, messages.paymentCollection),
    hasReceiptOption(intl, opts),
    companyOption(intl, opts)
  ];
}

export function createFilterStructureForDispatcher(
  intl: IntlShape,
  opts: RemittanceRunnerFilterOpts
): IFilter<RemittanceRunnerFilterKeys> {
  return [
    remittanceIdOption(intl, opts),
    statusOption(intl, opts),
    remittanceDateOption(intl, opts),
    dispatcherOption(intl, opts),
    createdDateOption(intl, opts),
    hasReceiptOption(intl, opts)
  ];
}

export function createFilterStructureForToRemit(
  intl: IntlShape,
  opts: RemittanceRunnerFilterOpts
): IFilter<RemittanceRunnerFilterKeys> {
  return [
    motherOrderOption(intl, opts),
    dateShippedOption(intl, opts),
    dateCompletedOption(intl, opts),
    deliveryTypeOption(intl, opts),
    riderNameOption(intl, opts),
    amountOption(intl, opts),
    forPaymentCollectionOption(intl, opts, messages.isPaymentCollected)
  ];
}

export function createFilterStructureForPaymentCollection(
  intl: IntlShape,
  opts: RemittanceRunnerFilterOpts
): IFilter<RemittanceRunnerFilterKeys> {
  return [
    motherOrderOption(intl, opts),
    dateShippedOption(intl, opts),
    dateCompletedOption(intl, opts),
    deliveryTypeOption(intl, opts),
    riderNameOption(intl, opts),
    amountOption(intl, opts)
  ];
}
